import { Directive, ElementRef, HostBinding, Input, NgZone, OnDestroy, OnInit } from '@angular/core';

/**
 * Adds `is-compact` to an element once its text runs past `appCompactPast` lines, so a stylesheet
 * can step the size down.
 *
 * MEASURED, NOT GUESSED FROM STRING LENGTH. The same promotion title is one line in English and
 * three in Macedonian, and the app's bundled Poppins carries no Cyrillic at all — Macedonian
 * renders in whatever the system substitutes, at metrics no character count could predict. Only the
 * laid-out box knows.
 *
 * A ResizeObserver rather than a one-off read after load, because the height changes without the
 * text changing: a language switch re-localizes in place, and a rotation re-wraps.
 *
 * Lives here rather than in either promotion screen because both of them need it and they are
 * separate components — the before-order sheet and the notification page share a composition, and
 * a rule that only one of them applied is exactly the kind of drift this replaces.
 */
@Directive({
    selector: '[appCompactPast]',
    standalone: false
})
export class CompactTitleDirective implements OnInit, OnDestroy {
  /** Line count the element is allowed before it counts as compact. */
  @Input('appCompactPast') maxLines = 2;

  @HostBinding('class.is-compact') compact = false;

  private observer?: ResizeObserver;

  /**
   * Width and text the live decision was taken for.
   *
   * What separates a resize we caused from one we must react to: stepping the font down changes
   * the height and nothing else, so an unchanged key means the observer is reporting our own work.
   */
  private decidedFor = '';

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngOnInit(): void {
    // No ResizeObserver (very old Safari) simply means no step-down — the title stays at its full
    // size and wraps, which is the behaviour these screens had before this existed.
    if (typeof ResizeObserver === 'undefined') return;

    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.host.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  /**
   * ⚠️ THE DECISION IS ONE-WAY, AND THAT IS WHAT STOPS IT BLINKING.
   *
   * The class this sets changes the size of the very box being measured, so "re-read the height
   * and answer again" has no fixed point for a whole band of titles: at 32px/1.08 the promo title
   * "Happy hour: half price on every cocktail until seven" is three lines and asks to be compact,
   * at 27px/1.14 it is two and asks to be full — measured, both screens, Latin and Cyrillic alike.
   * The old equality guard did not help; it only suppressed repeats of the SAME answer, while the
   * answer genuinely alternated, so the class flipped on every frame and the title strobed.
   *
   * So the rule only ever steps DOWN, and only from the full-size box — the one the rule is
   * actually about. A shrunk box never gets to argue for growing back.
   *
   * When the inputs really do change — a language switch re-localizes in place, a rotation
   * re-wraps — the step-down is released first so the next pass measures the base box again.
   * That costs one frame at full size, which is the price of measuring rather than guessing.
   */
  private measure(): void {
    const el = this.host.nativeElement;

    const key = `${Math.round(el.clientWidth)}|${el.textContent ?? ''}`;
    const inputsChanged = key !== this.decidedFor;
    this.decidedFor = key;

    if (inputsChanged && this.compact) {
      // Release and let the resulting resize bring us back with the base box to measure.
      this.zone.run(() => { this.compact = false; });
      return;
    }

    // `lineHeight` computes to "normal" on an element that has not been given one; there is no
    // number to divide by then, so leave the class alone rather than guess a ratio.
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
    if (!lineHeight) return;

    // Rounded before comparing: sub-pixel line boxes routinely make two lines measure 2.03.
    const lines = Math.round(el.clientHeight / lineHeight);
    if (this.compact || lines <= this.maxLines) return;

    // The observer fires outside Angular and this flips a host binding, so the write has to come
    // back inside. One-way, so it cannot loop against its own resize.
    this.zone.run(() => { this.compact = true; });
  }
}
